import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControl, FormLabel, Modal, ModalClose,
  ModalDialog, Option, Select, Stack, Textarea, Typography } from '@mui/joy';
import { APIGetClientRequestMisCandidates, APIConvertClientRequestToProfile } from '../../api';

export default function MisReviewDialog({ request, token, onClose, onApproved }) {
  const [verifiedContact, setVerifiedContact] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const epochRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    const epoch = ++epochRef.current;
    busyRef.current = false;
    setSaving(false); setSelected(null); setConfirmed(false);
    setNote(''); setCandidates([]); setError(''); setVerifiedContact('');
    if (!request?.id) return;
    setLoading(true);
    APIGetClientRequestMisCandidates(token, request.id).then((result) => {
      if (epochRef.current !== epoch) return;
      setVerifiedContact(result.contact || '');
      setCandidates(result.candidates || []);
    }).catch((error) => {
      if (epochRef.current === epoch)
        setError(error?.response?.data?.error || 'Не удалось получить карточки МИС');
    }).finally(() => {
      if (epochRef.current === epoch) setLoading(false);
    });
    return () => { epochRef.current += 1; };
  }, [request?.id, token, reload]);

  const save = async () => {
    const selectedCard = candidates.find((item) => item.mis_id === selected);
    if (!request?.id || busyRef.current || loading || !selectedCard || selectedCard.already_linked
        || !confirmed || note.trim().length < 10) return;
    const epoch = epochRef.current;
    busyRef.current = true; setSaving(true); setError('');
    try {
      const result = await APIConvertClientRequestToProfile(token, request.id, {
        mis_patient_id: selected, identity_confirmed: true, review_note: note.trim(),
      });
      if (epochRef.current !== epoch) return;
      if (!result.success) throw new Error(result.error || 'Заявка не подтверждена');
      onClose();
      await onApproved?.(result);
    } catch (error) {
      if (epochRef.current === epoch)
        setError(error?.response?.data?.error || error?.message || 'Не удалось обработать заявку');
    } finally {
      if (epochRef.current === epoch) {
        busyRef.current = false; setSaving(false);
      }
    }
  };

  return (
    <Modal open={Boolean(request)} onClose={() => { if (!saving) onClose(); }} sx={{ zIndex: 10040 }}>
      <ModalDialog sx={{ width: { xs: 'calc(100vw - 24px)', sm: 560 }, maxWidth: 560,
                        maxHeight: '90dvh', overflowY: 'auto', borderRadius: 'lg' }}>
        <ModalClose disabled={saving} />
        <Typography level="h4">Уточнить карточку МИС</Typography>
        <Typography level="body-sm">Заявитель: {request?.name}. Подтверждённый контакт: {verifiedContact || 'загружаем...'}</Typography>
        <Alert color="warning" variant="soft">
          Свяжитесь с заявителем и уточните ФИО и дату рождения. Один общий телефон не подтверждает,
          что все найденные карточки принадлежат одному человеку. Выберите только проверенную карточку.
        </Alert>
        {error && <Alert role="alert" color="danger">{error}</Alert>}
        <FormControl>
          <FormLabel>Карточка пациента</FormLabel>
          <Select value={selected} onChange={(_, value) => { setSelected(value); setConfirmed(false); }}
            disabled={loading || saving} placeholder={loading ? 'Загружаем карточки...' : 'Выберите карточку'}
            slotProps={{ listbox: { sx: { zIndex: 10041, maxHeight: 280 } } }}>
            {candidates.map((item) => (
              <Option key={item.mis_id} value={item.mis_id} disabled={item.already_linked || !item.name}>
                <Stack>
                  <Typography level="body-sm">{item.name || 'В МИС нет ФИО'}</Typography>
                  <Typography level="body-xs">Дата рождения: {item.birth_date || 'не указана'} · ID: {item.mis_id}
                    {item.already_linked ? ' · уже есть кабинет' : ''}</Typography>
                </Stack>
              </Option>
            ))}
          </Select>
        </FormControl>
        {!loading && !candidates.length && (
          <Alert color="warning">Подходящих карточек сейчас нет. Проверьте данные в МИС; автоматически одобрять заявку нельзя.</Alert>
        )}
        <Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)}
          disabled={!selected || saving || loading} label="Я проверил(а) личность и принадлежность выбранной карточки" />
        <FormControl>
          <FormLabel>Комментарий проверки</FormLabel>
          <Textarea value={note} onChange={(event) => setNote(event.target.value)} disabled={saving}
            minRows={2} placeholder="Например: уточнили ФИО и дату рождения, выбранная карточка подтверждена"
            slotProps={{ textarea: { maxLength: 500 } }} />
        </FormControl>
        <Typography level="body-xs">От 10 до 500 символов. Не записывайте сюда номера документов или медицинские сведения.</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" color="neutral" disabled={saving} onClick={onClose}>Отмена</Button>
          <Button variant="soft" disabled={saving || loading} onClick={() => setReload((value) => value + 1)}>Обновить карточки</Button>
          <Button loading={saving} disabled={loading || !selected || !confirmed || note.trim().length < 10}
            onClick={save}>Подтвердить заявку</Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}